/**
 * HairlineButton — secondary editorial CTA.
 * One-pixel outlined ghost button. On cream surfaces the border is charcoal;
 * on charcoal surfaces the border is gold-foil for a stamped-metal feel.
 *
 * @example
 *   <HairlineButton href="/marketing">See the long version</HairlineButton>
 *   <HairlineButton onClick={skip} arrow={false} tone="dark">Skip for now</HairlineButton>
 */
import React from "react";

const SIZE = {
  sm: "px-5 py-2.5 text-[11px]",
  md: "px-7 py-3.5 text-[12px]",
  lg: "px-9 py-4 text-[13px]",
};

function Arrow({ className = "" }) {
  return (
    <svg className={className} width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
      <path d="M0 5h12M8 1l4 4-4 4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="square" />
    </svg>
  );
}

export default function HairlineButton({
  href,
  onClick,
  type = "button",
  size = "md",
  arrow = true,
  tone = "light", // light → on cream; dark → on charcoal
  className = "",
  children,
  ...rest
}) {
  const inner = (
    <span className="inline-flex items-center gap-3">
      <span className="uppercase tracking-[0.22em] font-medium">{children}</span>
      {arrow ? <Arrow className="transition-transform duration-300 group-hover:translate-x-1" /> : null}
    </span>
  );

  const light =
    "text-[color:var(--charcoal)] border border-[color:var(--charcoal)]/85 hover:bg-[color:var(--charcoal)] hover:text-[color:var(--cream)] ed-focus";
  const dark =
    "text-[color:var(--cream)] border border-[color:var(--gold-foil)]/80 hover:bg-[color:var(--gold-foil)] hover:text-[color:var(--charcoal)] ed-focus-dark";

  const cls = `group inline-flex items-center justify-center font-sans rounded-none transition ${tone === "dark" ? dark : light} ${SIZE[size] || SIZE.md} ${className}`;

  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {inner}
      </a>
    );
  }
  return (
    <button type={type} onClick={onClick} className={cls} {...rest}>
      {inner}
    </button>
  );
}
